'use client';

import Image from '@/components/ui/CspImage';
import { Link } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import { scrollToSection } from '@/lib/scroll-to-section';

export default function SiteFooter() {
  const t = useTranslations();

  return (
    <footer className="border-border border-t bg-card">
      <div className="mx-auto max-w-[1920px] px-6 py-16 sm:px-10 lg:px-[120px]">
        <div className="mb-12 grid grid-cols-2 gap-8 md:grid-cols-3 xl:grid-cols-[minmax(32rem,2fr)_repeat(3,minmax(0,1fr))]">
          <div className="col-span-2 md:col-span-3 xl:col-span-1">
            <div className="-mt-5 mb-4">
              <Image
                src="/StyTrix_Logo_RGB_Orange.png"
                alt="StyTrix"
                width={250}
                height={66}
                className="block dark:hidden w-auto h-[66px]"
              />
              <Image
                src="/StyTrix_Logo_RGB_Orange.png"
                alt="StyTrix"
                width={250}
                height={66}
                className="hidden dark:block w-auto h-[66px]"
              />
            </div>
            <p className="mb-4 text-muted-foreground text-sm">
              {t('footer.brandDescription')}
            </p>
            <div className="flex gap-3">
              <a
                href="https://linkedin.com/company/stytrix"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary transition-all duration-200 hover:scale-110 hover:bg-primary hover:text-white"
                aria-label="LinkedIn"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
              <a
                href="https://www.instagram.com/stytrix.ai/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary transition-all duration-200 hover:scale-110 hover:bg-primary hover:text-white"
                aria-label="Instagram"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
            </div>
          </div>

          <div>
            <h4 className="mb-4 font-semibold text-foreground">{t('footer.product')}</h4>
            <ul className="space-y-3">
              <li>
                <Link href="/tools" className="whitespace-nowrap link-underline text-muted-foreground text-sm transition-colors hover:text-primary">
                  {t('footer.allTools')}
                </Link>
              </li>
              <li>
                <Link href="/#use-cases" onClick={scrollToSection} className="whitespace-nowrap link-underline text-muted-foreground text-sm transition-colors hover:text-primary">
                  {t('nav.useCases')}
                </Link>
              </li>
              <li>
                <Link href="/pricing" className="whitespace-nowrap link-underline text-muted-foreground text-sm transition-colors hover:text-primary">
                  {t('nav.pricing')}
                </Link>
              </li>
              <li>
                <Link href="/blog" className="whitespace-nowrap link-underline text-muted-foreground text-sm transition-colors hover:text-primary">
                  {t('nav.blog')}
                </Link>
              </li>
              <li>
                <Link href="/guide" className="whitespace-nowrap link-underline text-muted-foreground text-sm transition-colors hover:text-primary">
                  {t('footer.guide')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="mb-4 font-semibold text-foreground">{t('footer.company')}</h4>
            <ul className="space-y-3">
              <li>
                <Link href="/about" className="link-underline text-muted-foreground text-sm transition-colors hover:text-primary">
                  {t('footer.about')}
                </Link>
              </li>
              <li>
                <Link href="/careers" className="link-underline text-muted-foreground text-sm transition-colors hover:text-primary">
                  {t('footer.careers')}
                </Link>
              </li>
              <li>
                <Link href="/contact" className="link-underline text-muted-foreground text-sm transition-colors hover:text-primary">
                  {t('footer.contact')}
                </Link>
              </li>
              <li>
                <Link href="/press" className="link-underline text-muted-foreground text-sm transition-colors hover:text-primary">
                  {t('footer.press')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="mb-4 font-semibold text-foreground">{t('footer.legal')}</h4>
            <ul className="space-y-3">
              <li>
                <Link href="/privacy" className="whitespace-nowrap link-underline text-muted-foreground text-sm transition-colors hover:text-primary">
                  {t('footer.privacy')}
                </Link>
              </li>
              <li>
                <Link href="/subprocessors" className="whitespace-nowrap link-underline text-muted-foreground text-sm transition-colors hover:text-primary">
                  {t('footer.subprocessors')}
                </Link>
              </li>
              <li>
                <Link href="/terms" className="whitespace-nowrap link-underline text-muted-foreground text-sm transition-colors hover:text-primary">
                  {t('footer.terms')}
                </Link>
              </li>
              <li>
                <Link href="/cookies" className="whitespace-nowrap link-underline text-muted-foreground text-sm transition-colors hover:text-primary">
                  {t('footer.cookies')}
                </Link>
              </li>
              <li>
                <Link href="/accessibility" className="whitespace-nowrap link-underline text-muted-foreground text-sm transition-colors hover:text-primary">
                  {t('footer.accessibility')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-border border-t pt-8">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <div className="flex items-center gap-4 text-muted-foreground text-xs">
              <div className="flex items-center gap-1.5">
                <svg className="h-4 w-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>{t('footer.sslSecure')}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <svg className="h-4 w-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{t('footer.gdprCompliant')}</span>
              </div>
            </div>
            <div className="text-muted-foreground text-sm">
              {t('footer.copyright', { year: new Date().getFullYear() })}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
